import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Grid,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Autocomplete,
  useTheme,
  useMediaQuery,
  Chip,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../../../contexts/AuthContext';
import { WarehouseService, CreateInventoryWarehouseDto, InventoryWarehouseDto } from './services';
import { useTranslation } from 'react-i18next';

interface InventoryWarehouse {
  id: string;
  name: string;
  address?: string;
  description?: string;
  isDefault?: boolean;
  createdAt: string;
  branchesId?: string | null;
  warehouseType?: string;
  locationModel?: string;
  totalCapacity?: number;
  defaultRoutingStrategy?: string;
}

interface Branch {
  id: string;
  name: string;
}

interface InventoryWarehouseModalProps {
  open: boolean;
  onClose: () => void;
  branchId?: string;
  isManager?: boolean;
}

const InventoryWarehouseModal: React.FC<InventoryWarehouseModalProps> = ({ 
  open, 
  onClose, 
  branchId, 
  isManager = false 
}) => {
  const { t, i18n } = useTranslation('accounting');
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { branches, userDetails } = useAuth();
  
  const [warehouseName, setWarehouseName] = useState('');
  const [warehouseAddress, setWarehouseAddress] = useState('');
  const [warehouseDescription, setWarehouseDescription] = useState('');
  const [isDefaultWarehouse, setIsDefaultWarehouse] = useState(false);
  const [error, setError] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState<InventoryWarehouse | null>(null);
  const [warehouses, setWarehouses] = useState<InventoryWarehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [showInfoBox, setShowInfoBox] = useState<boolean>(false);
  
  // WMS alanları
  const [warehouseType, setWarehouseType] = useState<'Normal' | 'Smart'>('Normal');
  const [locationModel, setLocationModel] = useState('ZonePlatformRackLevelBin');
  const [defaultRoutingStrategy, setDefaultRoutingStrategy] = useState('SShapeSkip');

  // Silme onay modalı için state'ler
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [warehouseToDelete, setWarehouseToDelete] = useState<InventoryWarehouse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Şirket ID'sini, şubeleri ve depoları yükleme
  useEffect(() => {
    const loadCompanyAndData = async () => {
      try {
        setInitialLoading(true);
        
        const cId = userDetails?.companyId;
        setCompanyId(cId || null);
        
        if (!cId) {
          setInitialLoading(false);
          return;
        }
        
        // Depoları yükle (SQL'den)
        await loadInventoryWarehouses(cId);
        
      } catch (error) {
        console.error('Şirket veya veri yüklenirken hata:', error);
        setInitialLoading(false);
      }
    };
    
    if (open) {
      loadCompanyAndData();
    }
  }, [open, branchId, userDetails]);
  
  // ✅ Şube müdürü için otomatik şube seçimi (branches değiştiğinde)
  useEffect(() => {
    if (isManager && branchId && branches.length > 0) {
      const managerBranch = branches.find(branch => branch.id === branchId);
      if (managerBranch) {
        setSelectedBranch(managerBranch);
        console.log(`Şube müdürü için otomatik şube seçildi: ${managerBranch.name}`);
      }
    }
  }, [branches, isManager, branchId]);
  
  // Ürün depolarını yükleme (SQL'den)
  const loadInventoryWarehouses = async (companyId: string) => {
    try {
      console.log('✅ [SQL] Ürün depoları SQL\'den yükleniyor...');
      
      const warehousesData = await WarehouseService.getAllWarehouses(companyId);
      
      console.log('✅ [SQL] Depolar başarıyla yüklendi:', warehousesData.length);
      console.log('🔍 [DEBUG] Backend\'den gelen depolar:', warehousesData);
      
      // DTO'dan interface'e dönüştür
      const warehousesList: InventoryWarehouse[] = warehousesData.map((warehouse: InventoryWarehouseDto) => ({
        id: warehouse.id,
        name: warehouse.name || '',
        address: warehouse.address || '',
        description: warehouse.description || '',
        isDefault: warehouse.isDefault,
        createdAt: warehouse.createdAt || '',
        branchesId: warehouse.branchesId || null,
        warehouseType: warehouse.warehouseType || 'Normal',
        locationModel: warehouse.locationModel,
        totalCapacity: warehouse.totalCapacity,
        defaultRoutingStrategy: warehouse.defaultRoutingStrategy,
      }));
      
      console.log('✅ [SQL] İşlenmiş depolar:', warehousesList);
      
      // Depoları isme göre sıralayalım
      warehousesList.sort((a, b) => a.name.localeCompare(b.name));
      
      setWarehouses(warehousesList);
    } catch (error) {
      console.error('❌ [SQL] Depo verileri yüklenirken hata:', error);
      setSnackbarMessage(tt('Depolar yüklenirken hata oluştu', 'An error occurred while loading warehouses'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setInitialLoading(false);
      setLoading(false);
    }
  };
  
  // Formun temizlenmesi
  const resetForm = () => {
    setWarehouseName('');
    setWarehouseAddress('');
    setWarehouseDescription('');
    setIsDefaultWarehouse(false);
    setError('');
    setWarehouseType('Normal');
    setLocationModel('ZonePlatformRackLevelBin');
    setDefaultRoutingStrategy('SShapeSkip');
    if (!(isManager && branchId)) {
      setSelectedBranch(null);
    }
  };
  
  // Modal kapatılırken çalışacak handler
  const handleClose = () => {
    resetForm();
    setSelectedWarehouse(null);
    onClose();
  };
  
  // Depo seçimi
  const handleSelectWarehouse = (warehouse: InventoryWarehouse) => {
    console.log('🔍 [DEBUG] Seçilen depo:', warehouse);
    console.log('🔍 [DEBUG] IsDefault değeri:', warehouse.isDefault);
    
    setSelectedWarehouse(warehouse);
    setWarehouseName(warehouse.name);
    setWarehouseAddress(warehouse.address || '');
    setWarehouseDescription(warehouse.description || '');
    setIsDefaultWarehouse(warehouse.isDefault || false);
    setWarehouseType((warehouse.warehouseType === 'Smart' ? 'Smart' : 'Normal') as 'Normal' | 'Smart');
    setLocationModel(warehouse.locationModel || 'ZonePlatformRackLevelBin');
    // Eski kayıtlar 'SShape' olabilir; backend onları otomatik SShapeSkip'e map ediyor.
    setDefaultRoutingStrategy(warehouse.defaultRoutingStrategy || 'SShapeSkip');
    
    // Eğer bir depo seçildiyse, onun şubesini otomatik olarak seç
    if (warehouse) {
      if (warehouse.branchesId) {
        // Seçilen deponun şubesi varsa o şubeyi bul ve seç
        const warehouseBranch = branches.find(b => b.id === warehouse.branchesId);
        if (warehouseBranch) {
          setSelectedBranch(warehouseBranch);
        }
      }
    }
  };
  
  // Depo silme onay modalını aç
  const handleDeleteWarehouse = (warehouse: InventoryWarehouse, event: React.MouseEvent) => {
    event.stopPropagation();
    
    // Varsayılan depo silinemez
    if (warehouse.isDefault) {
      setSnackbarMessage(tt('Varsayılan depo silinemez', 'Default warehouse cannot be deleted'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    setWarehouseToDelete(warehouse);
    setDeleteConfirmOpen(true);
  };
  
  // Silme onay modalını kapat
  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setWarehouseToDelete(null);
  };
  
  // Depo silme işlemini onayla ve gerçekleştir
  const confirmDeleteWarehouse = async () => {
    if (!warehouseToDelete || !companyId) {
      return;
    }
    
    try {
      setIsDeleting(true);
      
      console.log('✅ [SQL] Depo siliniyor:', warehouseToDelete.id);
      
      // Depoyu SQL'den sil
      await WarehouseService.deleteWarehouse(companyId, warehouseToDelete.id);
      
      // Başarı mesajı
      setSnackbarMessage(tt('Depo başarıyla silindi', 'Warehouse deleted successfully'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Eğer silinen depo seçili depoysa, seçimi temizle
      if (selectedWarehouse?.id === warehouseToDelete.id) {
        setSelectedWarehouse(null);
        resetForm();
      }
      
      // Modal'ı kapat
      handleCloseDeleteConfirm();
      
      // Depoları yeniden yükle
      await loadInventoryWarehouses(companyId);
      
    } catch (error) {
      console.error('❌ [SQL] Depo silinirken hata:', error);
      setSnackbarMessage(tt('Depo silinirken bir hata oluştu', 'An error occurred while deleting warehouse'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Yeni depo ekleme/güncelleme (SQL)
  const handleAddWarehouse = async () => {
    if (!warehouseName.trim()) {
      setError(tt('Depo adı boş olamaz', 'Warehouse name cannot be empty'));
      return;
    }
    
    if (!companyId) {
      setError(tt('Şirket bilgisi bulunamadı', 'Company information not found'));
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('🔍 [DEBUG] isDefaultWarehouse state:', isDefaultWarehouse);
      
      if (selectedWarehouse) {
        const updateDto = {
          companyId: companyId,
          id: selectedWarehouse.id,
          name: warehouseName.trim(),
          address: warehouseAddress.trim() || undefined,
          description: warehouseDescription.trim() || undefined,
          isDefault: isDefaultWarehouse,
          isActive: true,
          branchesId: selectedBranch?.id || null,
          warehouseType: warehouseType,
          locationModel: warehouseType === 'Smart' ? locationModel : undefined,
          defaultRoutingStrategy: warehouseType === 'Smart' ? defaultRoutingStrategy : undefined,
        };
        await WarehouseService.updateWarehouse(updateDto);
        setSnackbarMessage(tt('Depo başarıyla güncellendi', 'Warehouse updated successfully'));
      } else {
        const createDto: CreateInventoryWarehouseDto = {
          companyId: companyId,
          name: warehouseName.trim(),
          address: warehouseAddress.trim() || undefined,
          description: warehouseDescription.trim() || undefined,
          isDefault: isDefaultWarehouse,
          branchesId: selectedBranch?.id || null,
          warehouseType: warehouseType,
          locationModel: warehouseType === 'Smart' ? locationModel : undefined,
          defaultRoutingStrategy: warehouseType === 'Smart' ? defaultRoutingStrategy : undefined,
        };
        await WarehouseService.createWarehouse(createDto);
        setSnackbarMessage(tt('Depo başarıyla eklendi', 'Warehouse added successfully'));
      }
      
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Formu sıfırla ve seçimi temizle
      resetForm();
      setSelectedWarehouse(null);
      
      // Depoları yeniden yükle
      await loadInventoryWarehouses(companyId);
      
    } catch (error) {
      console.error('❌ [SQL] Depo kaydedilirken hata:', error);
      setError(tt('Depo kaydedilirken bir hata oluştu', 'An error occurred while saving warehouse'));
      setSnackbarMessage(tt('Depo kaydedilirken bir hata oluştu', 'An error occurred while saving warehouse'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Şube adını getir
  const getBranchName = (branchId: string | null | undefined): string => {
    if (!branchId) return t('stock.modals.warehouse.general');
    
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : t('stock.modals.warehouse.unknownBranch');
  };
  
  // Buton metni
  const getButtonText = () => {
    if (loading) return t('stock.common.saving');
    return selectedWarehouse ? t('stock.modals.warehouse.actions.update') : t('stock.modals.warehouse.actions.add');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{t('stock.modals.warehouse.title')}</Typography>
          <IconButton edge="end" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ overflow: { xs: 'auto', md: 'hidden' }, p: 0 }}>
        <Box sx={{ p: { xs: 2, md: 3 }, height: { xs: 'auto', md: '100%' }, overflow: { xs: 'visible', md: 'hidden' } }}>
          {initialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
            {/* Form Alanı */}
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', height: '100%' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {selectedWarehouse ? t('stock.modals.warehouse.form.editTitle') : t('stock.modals.warehouse.form.addTitle')}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                {/* Şube seçim alanı */}
                <Autocomplete
                  fullWidth
                  value={selectedBranch}
                  onChange={(event, newValue) => {
                    if (!(isManager && branchId)) {
                      setSelectedBranch(newValue);
                    }
                  }}
                  options={branches}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', fontSize: '0.875rem' }} />
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('stock.modals.warehouse.form.branchOptional')}
                      size="small"
                      placeholder={t('stock.modals.warehouse.form.selectBranch')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        readOnly: isManager && branchId ? true : false
                      }}
                      disabled={!!(isManager && branchId)}
                      sx={{ mb: 2 }}
                    />
                  )}
                  disableClearable={!!(isManager && branchId)}
                  disabled={!!(isManager && branchId)}
                />
                
                <TextField
                  autoFocus
                  margin="dense"
                  id="warehouseName"
                  label={t('stock.modals.warehouse.form.name')}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={warehouseName}
                  onChange={(e) => {
                    setWarehouseName(e.target.value);
                    setError('');
                  }}
                  error={!!error}
                  helperText={error}
                  sx={{ mb: 2 }}
                  disabled={loading}
                />
                
                <TextField
                  margin="dense"
                  id="warehouseAddress"
                  label={t('stock.modals.warehouse.form.address')}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={warehouseAddress}
                  onChange={(e) => setWarehouseAddress(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={loading}
                  multiline
                  rows={2}
                />
                
                <TextField
                  margin="dense"
                  id="warehouseDescription"
                  label={t('stock.modals.warehouse.form.description')}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={warehouseDescription}
                  onChange={(e) => setWarehouseDescription(e.target.value)}
                  sx={{ mb: 2 }}
                  disabled={loading}
                />

                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <FormLabel component="legend">{t('stock.modals.warehouse.form.type')}</FormLabel>
                  <RadioGroup
                    row
                    value={warehouseType}
                    onChange={(e) => setWarehouseType(e.target.value as 'Normal' | 'Smart')}
                  >
                    <FormControlLabel value="Normal" control={<Radio size="small" />} label={t('stock.modals.warehouse.form.normal')} disabled={loading} />
                    <FormControlLabel value="Smart" control={<Radio size="small" />} label={t('stock.modals.warehouse.form.smart')} disabled={loading} />
                  </RadioGroup>
                </FormControl>

                <Collapse in={warehouseType === 'Smart'}>
                  <Box sx={{ mb: 2, p: 1.5, border: '1px dashed', borderColor: 'primary.light', borderRadius: 1 }}>
                    <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                      <InputLabel>{t('stock.modals.warehouse.form.locationModel')}</InputLabel>
                      <Select
                        value={locationModel}
                        label={t('stock.modals.warehouse.form.locationModel')}
                        onChange={(e) => setLocationModel(e.target.value)}
                        disabled={loading}
                      >
                        <MenuItem value="ZonePlatformRack">{t('stock.modals.warehouse.form.locationModel3')}</MenuItem>
                        <MenuItem value="ZonePlatformRackLevelBin">{t('stock.modals.warehouse.form.locationModel5')}</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('stock.modals.warehouse.form.routeStrategy')}</InputLabel>
                      <Select
                        value={defaultRoutingStrategy}
                        label={t('stock.modals.warehouse.form.routeStrategy')}
                        onChange={(e) => setDefaultRoutingStrategy(e.target.value)}
                        disabled={loading}
                      >
                        <MenuItem value="SShapeSkip">{t('stock.modals.warehouse.form.routeSShapeSkip')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Collapse>
                
                <Box sx={{ mt: 2, mb: 3, display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    id="isDefaultWarehouse"
                    checked={isDefaultWarehouse}
                    onChange={(e) => setIsDefaultWarehouse(e.target.checked)}
                    disabled={loading}
                    style={{ marginRight: 8 }}
                  />
                  <label htmlFor="isDefaultWarehouse">{t('stock.modals.warehouse.form.setDefault')}</label>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  {selectedWarehouse && (
                    <Button 
                      variant="outlined" 
                      onClick={() => {
                        setSelectedWarehouse(null);
                        resetForm();
                      }}
                      disabled={loading}
                    >
                      {t('stock.common.cancel')}
                    </Button>
                  )}
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleAddWarehouse}
                    disabled={!warehouseName.trim() || loading}
                    fullWidth={!selectedWarehouse}
                    sx={{ ml: selectedWarehouse ? 2 : 0 }}
                    startIcon={loading ? <CircularProgress size={24} /> : <AddIcon />}
                  >
                    {getButtonText()}
                  </Button>
                </Box>
              </Paper>
            </Grid>
            
            {/* Tablo/Kart Görünüm Alanı */}
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', height: '100%' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {t('stock.modals.warehouse.list.title')}
                  </Typography>
                  <Tooltip title={t('stock.modals.warehouse.infoBox.tooltip')}>
                    <IconButton
                      size="small"
                      onClick={() => setShowInfoBox(prev => !prev)}
                      sx={{
                        color: showInfoBox ? 'primary.main' : 'text.secondary',
                        border: '1px solid',
                        borderColor: showInfoBox ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        p: 0.5
                      }}
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {showInfoBox && (
                  <Alert severity="info" variant="outlined" sx={{ py: 0.5, px: 1, mb: 2 }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold' }}>
                      {t('stock.modals.warehouse.infoBox.title')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {t('stock.modals.warehouse.infoBox.description')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {t('stock.modals.warehouse.infoBox.item1')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {t('stock.modals.warehouse.infoBox.item2')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {t('stock.modals.warehouse.infoBox.item3')}
                    </Typography>
                  </Alert>
                )}
                
                <Divider sx={{ my: 2 }} />
                
                {warehouses.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('stock.modals.warehouse.list.empty')}
                  </Typography>
                ) : (
                  <>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('stock.modals.warehouse.list.total', { count: warehouses.length })}
                      </Typography>
                    </Box>
                    
                    {/* Desktop: Table View */}
                    {!isMobile && (
                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 400 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('stock.modals.warehouse.table.name')}</TableCell>
                              <TableCell>{t('stock.modals.warehouse.table.type')}</TableCell>
                              <TableCell>{t('stock.modals.warehouse.table.branch')}</TableCell>
                              <TableCell>{t('stock.modals.warehouse.table.status')}</TableCell>
                              <TableCell>{t('stock.modals.warehouse.table.address')}</TableCell>
                              <TableCell align="right">{t('stock.modals.warehouse.table.actions')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {warehouses.map((warehouse) => (
                              <TableRow 
                                key={warehouse.id} 
                                hover
                                sx={{ 
                                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                  ...(selectedWarehouse?.id === warehouse.id ? 
                                    { backgroundColor: 'rgba(25, 118, 210, 0.12)' } : {}),
                                  cursor: 'pointer'
                                }}
                                onClick={() => handleSelectWarehouse(warehouse)}
                              >
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <WarehouseIcon sx={{ 
                                      fontSize: '1rem', 
                                      mr: 1, 
                                      color: 'primary.main',
                                      opacity: 0.8
                                    }} />
                                    <Typography variant="body2" sx={{ 
                                      fontWeight: selectedWarehouse?.id === warehouse.id ? 'bold' : 'normal' 
                                    }}>
                                      {warehouse.name}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={warehouse.warehouseType === 'Smart' ? t('stock.modals.warehouse.form.smartShort') : t('stock.modals.warehouse.form.normal')}
                                    size="small"
                                    color={warehouse.warehouseType === 'Smart' ? 'info' : 'default'}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {getBranchName(warehouse.branchesId)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      bgcolor: warehouse.isDefault ? 'success.light' : 'text.disabled',
                                      color: warehouse.isDefault ? 'success.contrastText' : 'white',
                                      display: 'inline-block'
                                    }}
                                  >
                                    {warehouse.isDefault ? t('stock.modals.warehouse.table.default') : t('stock.modals.warehouse.form.normal')}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary" 
                                    sx={{ 
                                      maxWidth: 150,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {warehouse.address || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleDeleteWarehouse(warehouse, e)}
                                    sx={{ color: 'error.main' }}
                                    disabled={loading || warehouse.isDefault}
                                    title={warehouse.isDefault ? t('stock.modals.warehouse.messages.defaultCannotDelete') : t('stock.common.delete')}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                    
                    {/* Mobile: Card View */}
                    {isMobile && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {warehouses.map((warehouse) => (
                          <Card 
                            key={warehouse.id}
                            sx={{ 
                              border: selectedWarehouse?.id === warehouse.id 
                                ? '2px solid' 
                                : '1px solid',
                              borderColor: selectedWarehouse?.id === warehouse.id 
                                ? 'primary.main' 
                                : 'divider',
                              bgcolor: selectedWarehouse?.id === warehouse.id 
                                ? 'rgba(25, 118, 210, 0.08)' 
                                : 'background.paper',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleSelectWarehouse(warehouse)}
                          >
                            <CardContent sx={{ pb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <WarehouseIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography 
                                  variant="subtitle1" 
                                  fontWeight="bold"
                                  sx={{ flex: 1 }}
                                >
                                  {warehouse.name}
                                </Typography>
                                <Chip 
                                  label={warehouse.isDefault ? t('stock.modals.warehouse.table.default') : t('stock.modals.warehouse.form.normal')}
                                  size="small"
                                  color={warehouse.isDefault ? 'success' : 'default'}
                                />
                              </Box>
                              
                              {warehouse.address && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {t('stock.modals.warehouse.form.address')}:
                                  </Typography>
                                  <Typography variant="body2">
                                    {warehouse.address}
                                  </Typography>
                                </Box>
                              )}
                              
                              {warehouse.description && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {t('stock.modals.warehouse.form.description')}:
                                  </Typography>
                                  <Typography variant="body2">
                                    {warehouse.description}
                                  </Typography>
                                </Box>
                              )}
                            </CardContent>
                            
                            <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={(e) => handleDeleteWarehouse(warehouse, e)}
                                disabled={loading || warehouse.isDefault}
                              >
                                {warehouse.isDefault ? t('stock.modals.warehouse.table.cannotDelete') : t('stock.common.delete')}
                              </Button>
                            </CardActions>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Paper>
            </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={loading}>{t('stock.common.close')}</Button>
      </DialogActions>

      {/* Silme Onay Modalı */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">{t('stock.modals.warehouse.delete.title')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('stock.modals.warehouse.delete.irreversible')}
          </Alert>
          <Typography variant="body1" gutterBottom>
            <strong>"{warehouseToDelete?.name}"</strong> {t('stock.modals.warehouse.delete.confirmQuestion')}
          </Typography>
          {warehouseToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('stock.modals.warehouse.delete.details')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                • {t('stock.modals.warehouse.table.name')}: {warehouseToDelete.name}
              </Typography>
              {warehouseToDelete.address && (
                <Typography variant="body2">
                  • {t('stock.modals.warehouse.form.address')}: {warehouseToDelete.address}
                </Typography>
              )}
              {warehouseToDelete.description && (
                <Typography variant="body2">
                  • {t('stock.modals.warehouse.form.description')}: {warehouseToDelete.description}
                </Typography>
              )}
              <Typography variant="body2">
                • {t('stock.modals.warehouse.table.status')}: {warehouseToDelete.isDefault ? t('stock.modals.warehouse.table.default') : t('stock.modals.warehouse.form.normal')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCloseDeleteConfirm} 
            disabled={isDeleting}
            variant="outlined"
          >
            {t('stock.common.cancel')}
          </Button>
          <Button 
            onClick={confirmDeleteWarehouse} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? t('stock.modals.warehouse.delete.deleting') : t('stock.modals.warehouse.delete.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default InventoryWarehouseModal; 